import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase, type PortfolioProject } from '@/lib/supabase';

const CATEGORIES = ['All', 'Web App', 'Website', 'Backend', 'Full Stack'];

const CATEGORY_COLORS: Record<string, string> = {
  'Website': 'bg-violet-50 text-violet-700 border-violet-100',
  'Web App': 'bg-sky-50 text-sky-700 border-sky-100',
  'Backend': 'bg-rose-50 text-rose-700 border-rose-100',
  'Full Stack': 'bg-emerald-50 text-emerald-700 border-emerald-100',
  'Other': 'bg-gray-50 text-gray-700 border-gray-100',
};

export default function ProjectsPage() {
  const [projects, setProjects] = useState<PortfolioProject[]>([]);
  const [filtered, setFiltered] = useState<PortfolioProject[]>([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      const { data } = await supabase
        .from('portfolio_projects')
        .select('*')
        .order('sort_order', { ascending: true });
      if (data) {
        setProjects(data as PortfolioProject[]);
        setFiltered(data as PortfolioProject[]);
      }
      setLoading(false);
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    if (activeCategory === 'All') {
      setFiltered(projects);
    } else {
      setFiltered(projects.filter((p) => p.category === activeCategory));
    }
  }, [activeCategory, projects]);

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />

      {/* Page Header */}
      <section className="pt-32 pb-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-block w-6 h-px bg-emerald-400"></span>
            <span className="text-emerald-400 text-xs font-mono tracking-widest uppercase">Portfolio</span>
          </div>
          <h1
            className="text-4xl md:text-6xl font-bold text-white mb-4"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            All Projects
          </h1>
          <p className="text-gray-400 max-w-lg text-base">
            Every project here is something I've shipped — from side experiments to production systems used by thousands.
          </p>
        </div>
      </section>

      {/* Filter + Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-12">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-gray-900 text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-400 hover:text-gray-900'
                }`}
              >
                {cat}
                {cat === 'All' && (
                  <span className="ml-1.5 text-xs opacity-60">({projects.length})</span>
                )}
                {cat !== 'All' && (
                  <span className="ml-1.5 text-xs opacity-60">
                    ({projects.filter((p) => p.category === cat).length})
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="rounded-2xl bg-gray-200 animate-pulse h-80"></div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              <i className="ri-inbox-line text-5xl mb-4 block"></i>
              <p>No projects in this category yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((project) => (
                <article
                  key={project.id}
                  className="bg-white rounded-2xl overflow-hidden border border-gray-100 flex flex-col group hover:-translate-y-1 transition-transform duration-300"
                >
                  {/* Image */}
                  <div className="relative overflow-hidden h-52">
                    <img
                      src={project.image_url}
                      alt={project.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/10"></div>
                    <div className="absolute top-3 left-3 flex gap-2">
                      <span
                        className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${CATEGORY_COLORS[project.category] || CATEGORY_COLORS['Other']}`}
                      >
                        {project.category}
                      </span>
                      {project.featured && (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-100">
                          Featured
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 flex flex-col flex-1 gap-3">
                    <h2 className="font-bold text-gray-900 text-lg leading-snug">{project.title}</h2>
                    <p className="text-gray-500 text-sm leading-relaxed line-clamp-3 flex-1">
                      {project.description}
                    </p>

                    {/* Tech Stack */}
                    <div className="flex flex-wrap gap-1.5">
                      {project.technologies.map((tech) => (
                        <span
                          key={tech}
                          className="text-xs px-2 py-0.5 rounded-full bg-gray-50 text-gray-600 border border-gray-100 font-mono"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3 pt-2 border-t border-gray-50">
                      {project.demo_url && (
                        <a
                          href={project.demo_url}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white text-xs font-semibold rounded-full hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-external-link-line"></i>
                          Live Demo
                        </a>
                      )}
                      {project.github_url && (
                        <a
                          href={project.github_url}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-900 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-github-fill text-base"></i>
                          Source Code
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}